import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subdomain, setSubdomain] = useState(null);

  const detectTenant = useCallback(async () => {
    try {
      // Extract subdomain from current hostname
      const hostname = window.location.hostname;
      let detectedSubdomain = null;
      
      // Check for subdomain pattern: {slug}.mymeetingspal.com or {slug}.domain.com
      if (hostname.includes(".")) {
        const parts = hostname.split(".");
        // Skip common subdomains like www, app, preview
        if (parts.length >= 2 && !["www", "api", "app", "preview", "meetgov", "localhost"].includes(parts[0])) {
          detectedSubdomain = parts[0];
        }
      }
      
      // Also check URL param for development: ?tenant=slug
      const urlParams = new URLSearchParams(window.location.search);
      const tenantParam = urlParams.get("tenant");
      if (tenantParam) {
        detectedSubdomain = tenantParam;
      }
      
      setSubdomain(detectedSubdomain);
      
      if (detectedSubdomain) {
        // Fetch tenant info
        const response = await axios.get(`${API}/api/tenant/${detectedSubdomain}`);
        if (response.data.found) {
          setTenant(response.data.tenant);
          // Apply tenant branding to document
          applyBranding(response.data.tenant);
        }
      }
    } catch (error) {
      console.error("Failed to detect tenant:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    detectTenant();
  }, [detectTenant]);

  const applyBranding = (tenantData) => {
    if (!tenantData) return;
    
    // Set CSS custom properties for tenant branding
    const root = document.documentElement;
    
    if (tenantData.primary_color) {
      root.style.setProperty("--tenant-primary", tenantData.primary_color);
    }
    if (tenantData.secondary_color) {
      root.style.setProperty("--tenant-secondary", tenantData.secondary_color);
    }
    
    // Update page title
    if (tenantData.name) {
      document.title = `${tenantData.name} - MyMeetingsPal`;
    }
  };

  const setTenantManually = async (slug) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/tenant/${slug}`);
      if (response.data.found) {
        setTenant(response.data.tenant);
        setSubdomain(slug);
        applyBranding(response.data.tenant);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to set tenant:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const clearTenant = () => {
    setTenant(null);
    setSubdomain(null);
    // Reset CSS custom properties
    const root = document.documentElement;
    root.style.removeProperty("--tenant-primary");
    root.style.removeProperty("--tenant-secondary");
    document.title = "MyMeetingsPal";
  };

  const value = {
    tenant,
    subdomain,
    loading,
    isTenantContext: !!subdomain,
    setTenant: setTenantManually,
    clearTenant,
    refreshTenant: detectTenant,
    // Helper getters for branding
    primaryColor: tenant?.primary_color || "#0e4f55",
    secondaryColor: tenant?.secondary_color || "#14b8a6",
    logoUrl: tenant?.logo_url || null,
    tenantName: tenant?.name || "MyMeetingsPal",
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
}

export default TenantContext;
